import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { withApollo } from 'react-apollo';

import { hasSubtree } from '../NextTree/store/selectors';
import DynamicTreeBodyContent from './DynamicTreeBodyContent';
import DynamicTreeSearchBodyContent from './DynamicTreeSearchBodyContent';
import withNextTreeStoreConnect from '../NextTree/store/withNextTreeStoreConnect';

const SEARCH_STRING_MIN_CHAR_COUNT = 1;

@withApollo
@withNextTreeStoreConnect((state, { tree, selection, resolveNodeId }) => {
  const presentInTreeSelector = hasSubtree(tree);
  const presentInTree = id => presentInTreeSelector(state, id);

  return {
    hasSelectionInTree: extractLeafIds(selection, resolveNodeId).every(
      presentInTree,
    ),
  };
})
export default class DynamicTreeBody extends React.Component {
  static propTypes = {
    tree: PropTypes.string.isRequired,
    permission: PropTypes.string,
    selection: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    searchQuery: PropTypes.string.isRequired,

    hasSelectionInTree: PropTypes.bool.isRequired,

    treeAdapter: PropTypes.object.isRequired,
    treePlugins: PropTypes.array.isRequired,
    treeContext: PropTypes.any,
    rootModels: PropTypes.array,

    gqlQueries: PropTypes.shape({
      mainDataGqlQuery: PropTypes.object.isRequired,
      mainDataGqlVariables: PropTypes.object,
      formatMainVarsBeforeSend: PropTypes.func,
      formatAfterMainDataGqlQuery: PropTypes.func,

      searchDataGqlQuery: PropTypes.object,
      searchDataGqlVariables: PropTypes.object,
      formatSearchVarsBeforeSend: PropTypes.func,
      formatAfterSearchDataGqlQuery: PropTypes.func,
    }),

    resolveNodeId: PropTypes.func.isRequired,
    filter: PropTypes.object.isRequired,
    isSyntheticRootNodeAlwaysVisible: PropTypes.bool,
  };

  static defaultProps = {
    permission: null,
    selection: null,
    treeContext: null,
    rootModels: [],
    gqlQueries: {
      mainDataGqlVariables: {},
      formatMainVarsBeforeSend: null,
      formatAfterMainDataGqlQuery: null,
      searchDataGqlQuery: null,
      searchDataGqlVariables: {},
      formatSearchVarsBeforeSend: null,
      formatAfterSearchDataGqlQuery: null,
    },
    isSyntheticRootNodeAlwaysVisible: false,
  };

  state = {
    filter: {},
    isSearchQuery: false,
    preloadLeafIds: [],
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const { selection, hasSelectionInTree, searchQuery } = nextProps;

    const isSearchQuery = searchQuery.length >= SEARCH_STRING_MIN_CHAR_COUNT;
    const isFilterEquals = _.isEqual(prevState.filter, nextProps.filter);

    if (!hasSelectionInTree && selection !== prevState.selection) {
      return {
        isSearchQuery,
        selection,
        preloadLeafIds: extractLeafIds(selection, nextProps.resolveNodeId),
      };
    }

    if (isSearchQuery !== prevState.isSearchQuery) {
      return {
        isSearchQuery,
      };
    }

    if (!isFilterEquals) {
      return {
        filter: nextProps.filter,
      };
    }

    return null;
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      // eslint-disable react/prop-types
      this.state.preloadLeafIds !== nextState.preloadLeafIds ||
      (this.props.searchQuery !== nextProps.searchQuery &&
        (nextProps.searchQuery.length >= SEARCH_STRING_MIN_CHAR_COUNT ||
          nextProps.searchQuery.length === 0)) ||
      !_.isEqual(this.state.filter, nextState.filter)
    );
  }

  renderMainDataTree = () => {
    const {
      permission,
      gqlQueries: {
        mainDataGqlQuery,
        mainDataGqlVariables = {},
        formatMainVarsBeforeSend,
        formatAfterMainDataGqlQuery,
        mainDataGqlSkip,
      },
      filter,
      isSyntheticRootNodeAlwaysVisible,
    } = this.props;

    const { preloadLeafIds } = this.state;

    const initialVars = {
      searchQuery: '',
      ...mainDataGqlVariables,
      ...filter,
    };

    permission && (initialVars.permission = permission);

    const variables = formatMainVarsBeforeSend
      ? formatMainVarsBeforeSend(initialVars, filter)
      : initialVars;

    return (
      <DynamicTreeBodyContent
        formatAfterGqlQuery={formatAfterMainDataGqlQuery}
        gqlQuery={mainDataGqlQuery}
        gqlSkip={mainDataGqlSkip}
        gqlVariables={variables}
        isSyntheticRootNodeAlwaysVisible={isSyntheticRootNodeAlwaysVisible}
        loading={false}
        preloadLeafIds={preloadLeafIds}
      />
    );
  };

  renderSearchTree = () => {
    const {
      permission,
      searchQuery,
      treeAdapter,
      treePlugins,
      treeContext,
      rootModels,
      gqlQueries: {
        mainDataGqlQuery,
        searchDataGqlQuery,
        searchDataGqlVariables = {},
        mainDataGqlVariables = {},
        formatSearchVarsBeforeSend,
        formatMainVarsBeforeSend,
        formatAfterMainDataGqlQuery,
        formatAfterSearchDataGqlQuery,
      },
      filter,
    } = this.props;

    const formatData = searchDataGqlQuery
      ? formatSearchVarsBeforeSend
      : formatMainVarsBeforeSend;

    const query = searchDataGqlQuery || mainDataGqlQuery;

    const initialVars = {
      ...(searchDataGqlQuery ? searchDataGqlVariables : mainDataGqlVariables),
      ...filter,
      searchQuery,
    };

    permission && (initialVars.permission = permission);

    const variables = formatData ? formatData(initialVars) : initialVars;
    const formatAfterGqlQuery = formatAfterSearchDataGqlQuery
      ? formatAfterSearchDataGqlQuery
      : formatAfterMainDataGqlQuery;

    return (
      <DynamicTreeSearchBodyContent
        formatAfterGqlQuery={formatAfterGqlQuery}
        gqlQuery={query}
        gqlVariables={variables}
        loading={false}
        rootModels={rootModels}
        searchQuery={searchQuery}
        treeAdapter={treeAdapter}
        treeContext={treeContext}
        treePlugins={treePlugins}
      />
    );
  };

  render() {
    const { isSearchQuery } = this.state;

    return isSearchQuery ? this.renderSearchTree() : this.renderMainDataTree();
  }
}

function extractLeafIds(selection, resolveNodeId) {
  if (_.isEmpty(selection)) {
    return [];
  }

  if (!Array.isArray(selection)) {
    return [resolveNodeId(selection)];
  }

  return selection.map(resolveNodeId);
}
